import { takeLeading, call, put, delay } from 'redux-saga/effects';
import {
  signInRequest,
  signInSuccess,
  error,
  meRequest,
  meSuccess,
  initialLoadRequest,
  initialLoadSuccess,
  initialLoadUnauthenticated,
  logoutRequest,
  logoutSuccess,
  forgotPasswordRequest,
  forgotPasswordSuccess,
  resetPasswordRequest,
  resetPasswordSuccess,
  resetPasswordFailure,
} from '../../ducks/UserDucks';
import history from '../../../utils/history';
import { ROOT_PATH, REPORTS_PATH } from '../../../constants/routes';
import Authentication from '../../../utils/authentication';
import { enqueueSnackbar } from '../../ducks/SnackbarDucks';
import {
  setBugsnagUser,
  clearBugsnagUser,
} from '../../../utils/bugsnag/userContext';

// Bounded retry budget for the post-login profile fetch. The first attempt
// runs immediately so the happy path has no perceptible extra latency; the
// remaining attempts back off to absorb Eatzy API read-replica lag without
// stalling the UI when the token has already replicated. Total worst-case
// budget (~1s) is comparable to the previous hardcoded 1-second delay, but is
// only paid when actually needed.
export const POST_LOGIN_PROFILE_RETRY_DELAYS_MS = [0, 150, 350, 500];

function* fetchProfileAfterLogin() {
  let lastError;
  for (let i = 0; i < POST_LOGIN_PROFILE_RETRY_DELAYS_MS.length; i += 1) {
    const wait = POST_LOGIN_PROFILE_RETRY_DELAYS_MS[i];
    if (wait > 0) {
      yield delay(wait);
    }
    try {
      return yield call(Authentication.getProfile);
    } catch (err) {
      lastError = err;
      // A genuine role failure must not be retried — it will never succeed
      // and the user deserves immediate feedback.
      if (err?.message === 'Insufficient permissions') {
        throw err;
      }
    }
  }
  throw lastError || new Error('Unable to get profile');
}

function* signIn(action) {
  try {
    const { email, password } = action.payload;
    yield call(Authentication.login, email, password);
    const profile = yield call(fetchProfileAfterLogin);
    yield put(
      signInSuccess({
        user: profile,
      })
    );
    setBugsnagUser(profile);
    history.push(REPORTS_PATH);
  } catch (err) {
    yield put(
      enqueueSnackbar({
        message: err.message,
        options: {
          variant: 'error',
        },
      })
    );
    yield put(error({}));
    yield call(Authentication.logout);
  }
}

function* fetchUser() {
  try {
    const profile = yield call(Authentication.getProfile);
    yield put(
      meSuccess({
        user: profile,
      })
    );
    setBugsnagUser(profile);
  } catch (err) {
    yield put(
      enqueueSnackbar({
        message: err.message,
        options: {
          variant: 'error',
        },
      })
    );
    yield put(error({ message: err.message }));

    yield call(Authentication.logout);
  }
}

function* signOut(action) {
  const message = yield call(Authentication.logout, action.payload);
  yield put(logoutSuccess());
  clearBugsnagUser();
  if (message) {
    yield put(enqueueSnackbar(message));
  }
}

function* initialUserLoad() {
  try {
    const token = yield call(Authentication.getToken);
    if (!token) return yield put(initialLoadUnauthenticated());
    const profile = yield call(Authentication.getProfile);
    yield put(
      initialLoadSuccess({
        user: profile,
      })
    );
    setBugsnagUser(profile);
  } catch (err) {
    yield put(error({ message: err.message }));
  }
}

// Auth Hub no-enumeration contract: the response is always 200 with the same
// neutral copy regardless of whether an account exists. Mirror that on the
// client by showing the same neutral message and the same UI transition for
// success AND failure (network, 5xx, unexpected). We never reveal whether
// the email matched an account.
export const FORGOT_PASSWORD_NEUTRAL_MESSAGE =
  'If an account exists with this email, you\u2019ll receive a reset link shortly.';

// Friendly copy for documented Auth Hub error codes per the shared contract.
const RESET_ERROR_COPY = {
  INVALID_TOKEN:
    'This reset link is invalid or has expired. Request a new one.',
};
const RESET_ERROR_FALLBACK =
  'We couldn\u2019t reset your password. Please try again.';

function* forgotPassword(action) {
  const { email, redirectUrl } = action.payload;
  try {
    yield call(Authentication.forgotPasswordRequest, email, redirectUrl);
  } catch (_err) {
    // Swallow — the contract requires identical UX regardless of outcome.
  }
  yield put(forgotPasswordSuccess());
  yield put(
    enqueueSnackbar({
      message: FORGOT_PASSWORD_NEUTRAL_MESSAGE,
    })
  );
}

function* resetPassword(action) {
  try {
    const { token, password } = action.payload;
    yield call(Authentication.resetPasswordRequest, { token, password });
    yield put(resetPasswordSuccess());
    yield put(
      enqueueSnackbar({
        message: 'Password updated',
      })
    );
    history.push(ROOT_PATH);
  } catch (err) {
    const code = err?.data?.error;
    const field = err?.data?.field;
    const message =
      RESET_ERROR_COPY[code] ||
      (code === 'VALIDATION_ERROR'
        ? 'Please check the highlighted field and try again.'
        : RESET_ERROR_FALLBACK);
    // Field-targeted validation errors render inline on the form input;
    // everything else renders as a banner above the form. Either way we
    // never surface the raw machine-readable code to the user.
    yield put(
      resetPasswordFailure(
        code === 'VALIDATION_ERROR' && field ? { message, field } : { message }
      )
    );
  }
}

export default function* watchUser() {
  yield takeLeading(signInRequest, signIn);
  yield takeLeading(logoutRequest, signOut);
  yield takeLeading(meRequest, fetchUser);
  yield takeLeading(initialLoadRequest, initialUserLoad);
  yield takeLeading(forgotPasswordRequest, forgotPassword);
  yield takeLeading(resetPasswordRequest, resetPassword);
}
